/**
 * Centralized API Client with Advanced Features
 *
 * Features:
 * 1. Automatic Authorization header injection
 * 2. Automatic token refresh on 401 errors
 * 3. Request deduplication (prevents multiple identical requests)
 * 4. Automatic redirect to /login on auth failure
 * 5. Automatic redirect to /dashboard on 403 Forbidden errors
 * 6. Secure storage integration
 * 7. Request queuing during token refresh
 */

import axios from 'axios';
import secureStorage from './secureStorage';
import { AUTH_API as AUTH_API_URL, API_BASE as API_BASE_URL } from './apiConfig';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL
});

// Track refresh state to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

// Request deduplication cache
const pendingRequests = new Map();

/**
 * Generate unique key for request deduplication
 */
// Выбранная площадка (тот же источник, что в перехватчике запросов): URL ?hosting_id, затем localStorage
const readCurrentHosting = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('hosting_id');
    return fromUrl || localStorage.getItem('waymorr_current_hosting') || null;
  } catch (e) {
    return null;
  }
};

const getRequestKey = (config) => {
  const { method, url, params, data } = config;
  return JSON.stringify({
    method: method?.toLowerCase(),
    url,
    params,
    data,
    // Площадка подмешивается в запрос позже, перехватчиком: без неё в ключе запрос после смены площадки
    // считался «таким же» и получал ответ, начатый для прежней площадки.
    hosting: params?.hosting_id ?? readCurrentHosting()
  });
};

/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Redirect to login page
 */
const redirectToLogin = () => {
  // Clear all auth data
  secureStorage.clearAll();

  // Get current path to redirect back after login
  const currentPath = window.location.pathname;
  const returnUrl = currentPath !== '/login' ? `?returnUrl=${encodeURIComponent(currentPath)}` : '';

  // Redirect to login
  window.location.href = `/login${returnUrl}`;
};

/**
 * Redirect to dashboard (used for 403 errors)
 */
const redirectToDashboard = () => {
  console.warn('[API] Access forbidden (403), redirecting to dashboard');
  window.location.href = '/dashboard';
};

/**
 * Check if user is authenticated
 */
const checkAuth = () => {
  const token = secureStorage.getAccessToken();
  if (!token) {
    console.warn('[API] No access token found, redirecting to login');
    redirectToLogin();
    return false;
  }
  return true;
};

/**
 * Request interceptor - add Authorization header and handle deduplication
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Skip auth check for auth endpoints
    const isAuthEndpoint = config.url?.includes('/v1/auth/');

    if (!isAuthEndpoint) {
      // Check if user is authenticated
      if (!checkAuth()) {
        return Promise.reject(new Error('Not authenticated'));
      }
    }

    // Add Authorization header
    const token = secureStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Подмешиваем выбранный хостинг (для multi-hosting хостеров).
    // Приоритет: уже заданный config.params.hosting_id -> URL ?hosting_id -> localStorage.
    // Бэкенд игнорирует hosting_id у юзеров без user_hostings, поэтому безопасно для всех ролей.
    if (config.method?.toLowerCase() === 'get' && !isAuthEndpoint) {
      const params = config.params || {};
      if (params.hosting_id == null) {
        let hostingId = null;
        try {
          const urlParams = new URLSearchParams(window.location.search);
          hostingId = urlParams.get('hosting_id');
        } catch (e) {}
        if (!hostingId) {
          try {
            hostingId = localStorage.getItem('waymorr_current_hosting');
          } catch (e) {}
        }
        if (hostingId) {
          config.params = { ...params, hosting_id: hostingId };
        }
      }
    }

    // Add request key to config for cleanup in response interceptor
    if (config.method?.toLowerCase() === 'get' && config.deduplication !== false) {
      config._requestKey = getRequestKey(config);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handle 401, 403 and cleanup deduplication cache
 */
apiClient.interceptors.response.use(
  (response) => {
    // Cleanup deduplication cache for this request
    if (response.config._requestKey) {
      pendingRequests.delete(response.config._requestKey);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Cleanup deduplication cache on error
    if (originalRequest?._requestKey) {
      pendingRequests.delete(originalRequest._requestKey);
    }

    // Ошибки от auth-эндпоинтов (login/register/refresh/password) обрабатывает
    // компонент, не делаем автоматический refresh/redirect
    const isAuthEndpoint = originalRequest?.url?.match(/\/v1\/auth\/(login|register|refresh|password)/);
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // На этих эндпоинтах 403 означает бизнес-отказ, а не потерю доступа к странице
    const isBusinessForbidden = originalRequest?.url?.match(/\/(access-control\/role|client-requisites|hosting\/requisites|hosting_power|hosting_bill\/preview|devices\/[^/]+\/billing_power)/);

    if (error.response?.status === 403) {
      if (!isBusinessForbidden) {
        redirectToDashboard();
      }
      return Promise.reject(error);
    }

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    // Mark request as retried
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = secureStorage.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      console.log('[API] Access token expired, refreshing...');

      // Call refresh endpoint
      const response = await axios.post(`${AUTH_API_URL}/v1/auth/refresh`, {
        token: refreshToken
      });

      const { access_token, refresh_token } = response.data;

      // Store new tokens
      secureStorage.setAccessToken(access_token);

      // Refresh token may rotate (Supabase behavior)
      if (refresh_token) {
        secureStorage.setRefreshToken(refresh_token);
      }

      console.log('[API] Token refreshed successfully');

      // Update authorization header for original request
      originalRequest.headers.Authorization = `Bearer ${access_token}`;

      // Process queued requests
      processQueue(null, access_token);

      // Retry original request
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear tokens and redirect to login
      console.error('[API] Token refresh failed:', refreshError);

      processQueue(refreshError, null);

      // Dispatch custom event for AuthContext to handle
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'token_refresh_failed' }
      }));

      // Redirect to login
      redirectToLogin();

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Make API request with deduplication support
 */
const makeRequest = async (config) => {
  const requestKey = getRequestKey(config);

  // Check if identical request is pending (for GET requests)
  if (config.method?.toLowerCase() === 'get' && config.deduplication !== false && pendingRequests.has(requestKey)) {
    console.log('[API] Deduplicating request:', config.url);
    return pendingRequests.get(requestKey);
  }

  // Create request promise
  const requestPromise = apiClient(config);

  // Store promise for GET requests (only if deduplication is enabled)
  if (config.method?.toLowerCase() === 'get' && config.deduplication !== false) {
    pendingRequests.set(requestKey, requestPromise);

    // Cleanup after request completes (success or failure)
    requestPromise
      .finally(() => {
        setTimeout(() => {
          pendingRequests.delete(requestKey);
        }, 100); // Small delay to allow concurrent requests to deduplicate
      });
  }

  return requestPromise;
};

/**
 * Convenient HTTP methods
 */
const api = {
  get: (url, config = {}) => makeRequest({ ...config, method: 'get', url }),
  post: (url, data, config = {}) => makeRequest({ ...config, method: 'post', url, data }),
  put: (url, data, config = {}) => makeRequest({ ...config, method: 'put', url, data }),
  patch: (url, data, config = {}) => makeRequest({ ...config, method: 'patch', url, data }),
  delete: (url, config = {}) => makeRequest({ ...config, method: 'delete', url }),

  // Request without deduplication
  getWithoutCache: (url, config = {}) => makeRequest({ ...config, method: 'get', url, deduplication: false }),
};

/**
 * Helper function to create auth headers
 * Use this for requests that don't go through apiClient
 */
export const getAuthHeaders = () => {
  const token = secureStorage.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Helper to check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!secureStorage.getAccessToken();
};

/**
 * Clear pending requests cache
 * Useful when logging out or switching users
 */
export const clearPendingRequests = () => {
  pendingRequests.clear();
};

// Export both the raw axios instance and the convenient methods
export default api;
export { apiClient };
